import {
  API_ENDPOINTS,
  COPY_CALC_ICON,
  Mode,
  QUANTITY_SPEC_CODE,
  TURNAROUND_SPEC_CODE,
} from './constants';
import { createElement, ElementInfo } from './dom-util';
import { StateManager } from './state-manager';
import {
  CalcItemProduct,
  CalcItems,
  CalcItemSpec,
  CalcItemSpecs,
  CalculatorItem,
  CalculatorOptions,
  CalculatorResult,
  ParsedSku,
  ProductSpecs,
  State,
} from './types';
import { getCookie } from './utils/cookieHelper';
import { formatPhoneNumber } from './utils/format-phone-number';
import { SKU } from './utils/sku';

const flashLogo = `
<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M10 20C15.522 20 20 15.522 20 10C20 4.47803 15.522 0 10 0C4.47803 0 0 4.47803 0 10C0 15.522 4.47803 20 10 20ZM6.6885 9.91591L8.98551 12.2129V4.73543C8.98551 4.44472 9.22811 4.20213 9.51882 4.20213H10.4812C10.7719 4.20213 11.0145 4.44173 11.0145 4.73543V12.2129L13.3114 9.91591C13.5191 9.70827 13.858 9.70827 14.0657 9.91591L14.7461 10.5964C14.9538 10.8041 14.9513 11.1454 14.7461 11.3506L10.4528 15.6439C10.3313 15.7654 10.1612 15.815 10 15.7925C9.83882 15.815 9.66858 15.7654 9.54709 15.6439L5.25378 11.3506C5.04858 11.1454 5.04614 10.8041 5.25378 10.5964L5.93425 9.91591C6.14197 9.70827 6.48079 9.70827 6.6885 9.91591Z" fill="#2D264B"/>
</svg>

`;

/**
 * Calculator Implementation
 */
export class Calculator {
  readonly code: string;
  readonly mode: Mode;
  readonly modAdmin: boolean = false;
  readonly stateMng: StateManager<State>;
  private readonly options = {
    calculatorAPIUrl: 'http://localhost:3002/api/design-store', // default API URL
    apiUrl: 'http://localhost:3002/api/design-store',
    adminUrl: 'https://3d-dashboard.dev.truetale.io', // default admin url
  };

  constructor(code: string, mode: Mode, options?: CalculatorOptions) {
    this.stateMng = new StateManager<State>({
      items: [],
      stage: options?.stage,
      priceStatus: 'INCREASE',
      enableQuantitySwitcher: null,
      visibleHiddenProducts: {
        hidden: [],
        state: 'collapse',
        visible: [],
      },
      patternSets: [],
      colorThemes: [],
      selectedPatternSet: null,
      selectedColorTheme: null,
      defaultColorTheme: options?.defaultColorTheme ? +options?.defaultColorTheme : undefined,
      defaultPatternSet: options?.defaultPatternSet ? +options?.defaultPatternSet : undefined,
      defaultPrdSizeSpec: options?.defaultPrdSizeSpec,
      customColorTheme: null,
      calcItems: {
        turnaround: 'reg',
        products: {},
      },
      turnarounds: {
        reg: {
          period: 0,
          cost: 0,
        },
        productSpecs: {},
      },
      total: 0,
      comparePricePercentage: 0,
      comparePrice: 0,
      SKUs: {},
      title: '',
      productGroups: [],
      soCollectionHintText: options?.soCollectionHintText || '',
      isDesignServiceDisabled: options?.isDesignServiceDisabled || false,
      isApparel: false,
      bundleCode: '',
      bundleShortTitle: '',
      actions: [],
      currency: options?.currency ?? {
        code: 'USD',
        rate: 1,
        symbol: '$',
      },
      contentLoaded: false,
      rawData: null,
      paramSKUs: [],
      shopCode: options?.shopCode || '',
      phoneNumber: '',
    });

    // listen to the state changes
    this.stateMng.subscribe(
      'calcItems',
      [this.handleCalcItemChange, this.__renderCalcItemSelections, this.__renderPriceTableDOMTree],
      this,
    );
    this.stateMng.subscribe('productGroups', [this.__renderGroups], this);
    this.stateMng.subscribe('patternSets', [this.__renderPatternSets], this);
    this.stateMng.subscribe('colorThemes', [this.__renderColorThemes], this);
    this.stateMng.subscribe('turnarounds', this.__renderTurnaround, this);
    this.stateMng.subscribe('currency', [this.populateInitialUIValues], this);
    this.stateMng.subscribe('total', this.__renderTotal, this);
    this.stateMng.subscribe('comparePrice', this.__renderComparePrice, this);
    this.stateMng.subscribe('title', this.__renderTitle, this);
    this.stateMng.subscribe('actions', this.__renderActions, this);
    if (mode === Mode.SINGLE) {
      const sku = SKU.parse(code);
      this.stateMng.set('SKUs', { [sku.code]: code });
      code = sku.code;
    }
    this.code = code;
    this.mode = mode;
    this.options = { ...this.options, ...options };
    const isAdminMode = getCookie('DA_TOKEN');
    this.modAdmin = !!isAdminMode;
  }

  /**customer_invitations
   * @param actions HTMLElement[] | ElementInfo[]
   */
  setActions(actions: HTMLElement[] | ElementInfo[]) {
    if (!(actions instanceof Array)) {
      console.error('Actions must be an array of HTMLElement or ElementInfo');
      return;
    }

    if (actions.length === 0) return;
    this.stateMng.set(
      'actions',
      actions.map((action) => {
        if (action instanceof HTMLElement) return action;
        return createElement(action);
      }),
    );
  }

  protected getStartContainerDOMTree(): ElementInfo {
    return {
      tag: 'div',
      attributes: { class: 'start-here-container' },
      children: [
        {
          tag: 'div',
          attributes: {
            id: 'start-container-logo',
          },
          children: [],
        },
        {
          tag: 'p',
          attributes: {},
          children: ['Start Here'],
        },
      ],
    };
  }

  protected getHeaderDOMTree(): ElementInfo {
    const children: ElementInfo[] = [
      {
        tag: 'p',
        attributes: {
          class: 'calc-header__title',
          id: 'calc-header-title',
        },
        children: [this.mode === Mode.SINGLE ? 'Customize Your Product' : 'Customize Your Bundle'],
      },
    ];

    if (this.modAdmin && (this.mode === Mode.SINGLE || this.mode === Mode.PARENT_PRODUCT)) {
      children.push(
        {
          tag: 'span',
          attributes: { class: 'calc-header__title__templateUrl', id: 'tmpl_url_single_pdp' },
          children: [],
        },
        {
          tag: 'span',
          attributes: {
            class: 'calc-header__title__templateUrl',
            id: 'admin_product_url_single_pdp',
          },
          children: [],
        },
      );
    }

    if (this.modAdmin && this.mode === Mode.BUNDLE) {
      children.push({
        tag: 'span',
        attributes: { class: 'calc-header__title__templateUrl', id: 'admin_bundle_url_bundle_pdp' },
        children: [],
      });
    }

    children.push({
      tag: 'span',
      attributes: {
        class: 'calc-header-title-copy',
        id: 'calc-header-title-copy',
      },
      events: {
        click: this.copyCalcLinkWithSKUs.bind(this),
      },
      children: [],
    });

    return {
      tag: 'div',
      attributes: {
        class: 'calc-header',
      },
      children,
    };
  }

  protected copyCalcLinkWithSKUs() {
    navigator.clipboard.writeText('');
    const skus = this.stateMng.get('SKUs');
    const patternSetCode = this.stateMng.get('selectedPatternSet')?.code;
    const colorThemeCode = this.stateMng.get('selectedColorTheme')?.code;
    const skuText = Object.values(skus).join('__');
    const domain = window.location.href.split('?')[0];
    if (patternSetCode && colorThemeCode)
      navigator.clipboard.writeText(
        `${domain}?sku=${skuText}:::PS:${patternSetCode}-CT:${colorThemeCode}`,
      );
    else navigator.clipboard.writeText(`${domain}?sku=${skuText}`);
  }

  protected getBodyDOMTree(): ElementInfo {
    return {
      tag: 'div',
      attributes: { class: 'calc-body' },
      children: (() => {
        if (this.mode === Mode.SINGLE) {
          const item = this.stateMng.get('items')[0];
          return this.getSingleDOMTree(item);
        }

        return this.getBundleDOMTree(this.stateMng.get('items'));
      })(),
    };
  }

  protected getFooterDOMTree(): ElementInfo {
    return {
      tag: 'div',
      attributes: { class: 'calc-footer' },
      children: [
        {
          tag: 'div',
          attributes: { class: 'calc-footer__turnaround' },
          children: [
            {
              tag: 'p',
              attributes: { class: 'calc-footer__turnaround__title' },
              children: ['Production Turnaround'],
            },
            {
              tag: 'div',
              attributes: { class: 'calc-footer__turnaround__options' },
              children: this.getTurnaroundDOMTree(),
            },
          ],
        },
        { tag: 'hr', attributes: { class: 'divider' } },
        {
          tag: 'div',
          attributes: { class: 'calc-footer__total' },
          children: [
            { tag: 'span', children: ['Total'] },
            {
              tag: 'span',
              attributes: { id: 'calc-total' },
              children: [`$0`],
            },
          ],
        },
        {
          tag: 'div',
          attributes: { class: 'calc-footer__actions', id: 'calc-footer-actions' },
          children: [''],
        },
      ],
    };
  }

  render(containerId: string) {
    const container = document.getElementById(containerId);
    if (!container) throw new Error('Container not found');

    if (!container.classList.contains('calc-container')) container.classList.add('calc-container');

    container.append(createElement(this.getStartContainerDOMTree()));
    const startContainer = document.getElementById('start-container-logo');
    if (startContainer) startContainer.innerHTML = flashLogo;
    container.append(createElement(this.getHeaderDOMTree()));
    const requestId = Date.now() + '-' + Math.random().toString(36).substring(2, 10);
    this.load(requestId)
      .then(() => {
        container.append(createElement(this.getBodyDOMTree()));
        container.append(createElement(this.getFooterDOMTree()));
        this.populateInitialUIValues();
        this.stateMng.set('contentLoaded', true);
      })
      .catch((e) => {
        console.error('Calculator caught error', e, requestId);
        container.innerHTML = '';
        container.append(createElement(this.getErrorDOMTree()));
      });
  }

  submit(): CalculatorResult {
    const SKUs = this.stateMng.get('SKUs');
    const calcItems = this.stateMng.get('calcItems');
    const items = Object.keys(calcItems.products)
      .filter((key) => key in SKUs)
      .reduce(
        (acc, key) => {
          const product = calcItems.products[key];
          acc[key] = { qty: product.qty, sku: SKUs[key] };
          return acc;
        },
        <CalculatorResult['items']>{},
      );
    return {
      turnaroundType: calcItems.turnaround,
      items: items,
    };
  }

  onContentLoaded(callback: () => void) {
    this.stateMng.subscribe('contentLoaded', callback);
  }

  protected getParamSKUs(): ParsedSku[] {
    const urlSKUs: ParsedSku[] = [];
    if (typeof window !== 'undefined') {
      const param = new URLSearchParams(window.location.search);
      const skuParam = param.get('sku');

      const splittedCode = skuParam?.split(':::') || [];
      //'CHECKERED_KIT:::PS=CHECKERED-CT=TOKYO'
      const defaultPatternSet =
        splittedCode?.length > 1 ? +splittedCode[1]?.split('-')[0]?.split(':')[1] : undefined;
      const defaultColorTheme =
        splittedCode?.length > 1 ? +splittedCode[1]?.split('-')[1]?.split(':')[1] : undefined;

      const prdWithSizeSpecs: { [productCode in string]: string } = {};
      splittedCode[1]
        ?.split('-')
        .slice(2)
        .map((code) => {
          const [productCode, sizeSpecValue] = code.split(':');
          prdWithSizeSpecs[productCode] = sizeSpecValue;
        });

      if (defaultPatternSet) this.stateMng.set('defaultPatternSet', defaultPatternSet);
      if (defaultColorTheme) this.stateMng.set('defaultColorTheme', defaultColorTheme);

      if (skuParam) {
        const skus = skuParam.split('__');
        skus.forEach((sku) => {
          try {
            urlSKUs.push(SKU.parse(sku));
          } catch (e) {
            console.error(`failed to parse param sku ${sku}`, e);
          }
        });
      }
    }
    return urlSKUs;
  }

  load(requestId: string): Promise<void> {
    console.log('🟢 [load] Starting load function...', requestId);
    const paramSKUs = this.getParamSKUs();
    console.log('📦 [load] Param SKUs:', paramSKUs);

    let productSKUCounters: Record<string, number> = {};

    const getProductDefaultSpecValue = (productCode: string, counter: number): ParsedSku => {
      const sku = paramSKUs.filter((sku) => sku.code.toLowerCase() === productCode.toLowerCase());
      console.log(
        `🔍 [getProductDefaultSpecValue] productCode=${productCode}, counter=${counter}, matched SKUs:`,
        sku,
      );
      return sku?.[counter];
    };
    const prepareCalcProd = (
      prod: any,
      details: {
        qty: number;
        selected: boolean;
        ta?: string;
        parsedSku: ParsedSku;
        groupId?: string;
      },
    ) => {
      console.log('⚙️ [prepareCalcProd] Preparing calc product:', prod.code, 'details:', details);

      const productSpecs = prod.specs || [];

      const specs = productSpecs.reduce((acc: CalcItemSpecs, spec: ProductSpecs) => {
        const p = details.parsedSku.specs.find((s) => s.code === spec.code);
        const value = p
          ? spec.specsValue.find((value) =>
              details.ta && p.code === TURNAROUND_SPEC_CODE
                ? value.code === details.ta.toUpperCase()
                : value.code === p.value,
            ) || spec.specsValue[0]
          : spec.specsValue[0];

        if (!value) {
          console.warn(`⚠️ [prepareCalcProd] No value found for spec ${spec.code}`);
          return acc;
        }

        if (spec.code === QUANTITY_SPEC_CODE) details.qty = +value.code;

        const nonHideCalcValue = value.hideInCalc
          ? spec.specsValue.find((sv) => !sv.hideInCalc)
          : value;

        if (nonHideCalcValue)
          acc[spec.code] = {
            valueCode: nonHideCalcValue.code,
            adderType: nonHideCalcValue.adderType,
            adderValue: nonHideCalcValue.adderValue,
          };

        return acc;
      }, {});

      console.log('✅ [prepareCalcProd] Specs prepared for', prod.code, specs);

      const shopCode = this.stateMng.get('shopCode');
      let multiplierPercentage;
      const validShopCodes = ['ex', 'mi', 'vt', 'ap'];

      if (validShopCodes.includes(shopCode || 'ex')) {
        multiplierPercentage = prod.productStores?.find(
          (store: any) => store.shopCode === shopCode,
        )?.multiplierPercentage;
        console.log(
          `🏬 [prepareCalcProd] ShopCode=${shopCode}, multiplier=${multiplierPercentage}`,
        );
      } else {
        console.error('❌ Invalid shop code:', shopCode);
        throw new Error('Invalid shop code');
      }

      return {
        code: prod.code,
        basePrice: +prod.basePrice,
        qty: details.qty,
        selected: details.selected,
        printable: prod.isPrintable,
        multiplierPercentage,
        groupId: details.groupId,
        specs,
      };
    };

    console.log('🚦 [load] Mode detected:', this.mode);

    this.fetchAndSetPhoneNumber();

    // ===============================
    // 🧩 SINGLE PRODUCT MODE
    // ===============================
    if (this.mode === Mode.SINGLE) {
      console.log('🧩 [SINGLE] Fetching product...', requestId);
      // Helper: fetch with retry (maxAttempts = 3 by default, 1000ms delay)
      const fetchWithRetry = (
        url: string,
        options: RequestInit = {},
        maxAttempts = 3,
        retryDelay = 1000,
      ): Promise<any> =>
        new Promise((resolve, reject) => {
          let attempts = 0;
          const tryFetch = () => {
            attempts++;
            console.log(
              `🌐 [SINGLE] Fetch attempt ${attempts}/${maxAttempts} -> ${url}`,
              requestId,
            );
            const addedParam = attempts > 1 ? `?retry=${attempts}` : '';
            fetch(url + addedParam)
              .then((res) => {
                console.log('🌐 [SINGLE] Response status:', res.status, requestId);
                if (res.status === 200) {
                  return res.json().then(resolve);
                }
                return res
                  .json()
                  .then((errBody) => {
                    const err: any = new Error('Non-200 response');
                    err.status = res.status;
                    err.body = errBody;
                    throw err;
                  })
                  .catch((parseErr) => {
                    const err: any = new Error('Non-200 response (and body parse failed)');
                    err.status = res.status;
                    err.parseErr = parseErr;
                    throw err;
                  });
              })
              .catch((err) => {
                if (attempts < maxAttempts) {
                  console.warn(
                    `⚠️ [SINGLE] Attempt ${attempts} failed. Retrying in ${retryDelay}ms...`,
                    err,
                    requestId,
                  );
                  setTimeout(tryFetch, retryDelay);
                } else {
                  console.error('❌ [SINGLE] All attempts failed.', requestId);
                  reject(err);
                }
              });
          };
          tryFetch();
        });

      return fetchWithRetry(
        `${this.options.calculatorAPIUrl}${API_ENDPOINTS.PRODUCT.replace(':0', this.code)}`,
        {
          method: 'GET',
          headers: {
            'X-Request-ID': requestId,
          },
        },
        3,
        1000,
      )
        .then((product: any) => {
          console.log('✅ [SINGLE] Product loaded:', product.code, requestId);
          this.stateMng.set('title', `Customize Your ${product.shortTitle}`);
          this.stateMng.set('isApparel', product.isApparel);
          const defaultSpecsFromParams = getProductDefaultSpecValue(product.code, 0);

          const parsedSku =
            defaultSpecsFromParams || SKU.parse(this.stateMng.get('SKUs')[product.code]);
          console.log('🔢 [SINGLE] Parsed SKU:', parsedSku, requestId);

          if (product?.isApparel) {
            // Collect all face pattern types from bundle products
            const facePatternTypes =
              product.modelSection.surfaces?.map((surface: any) => surface.facePatternTypeCode) ||
              [];
            fetch(`${this.options.calculatorAPIUrl}${API_ENDPOINTS.PATTERN_SETS}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ patternFaceTypes: facePatternTypes }),
            })
              .then(async (res) => {
                return res.json();
              })
              .then((data) => {
                const defaultPatternSetCode = this.stateMng.get('defaultPatternSet');

                const patternSet = defaultPatternSetCode
                  ? data.find((ps: any) => ps.code === defaultPatternSetCode)
                  : data[data?.length - 1];

                this.stateMng.set('selectedPatternSet', patternSet);
                this.stateMng.set('patternSets', data);
                setTimeout(() => {
                  window.dispatchEvent(
                    new CustomEvent('selectedPatternSet', {
                      detail: {
                        ...patternSet,
                      },
                    }),
                  );
                }, 3000);

                console.log('✅ Pattern sets loaded:', data);
                const defaultColorTheme = this.stateMng.get('defaultColorTheme');
                const colorTheme = defaultColorTheme
                  ? patternSet?.colorThemes?.find((ps: any) => ps.code === defaultColorTheme)
                  : patternSet?.colorThemes?.[0];
                this.stateMng.set('selectedColorTheme', colorTheme || null);
                this.stateMng.set('colorThemes', patternSet.colorThemes);
                setTimeout(() => {
                  window.dispatchEvent(
                    new CustomEvent('selectedColorTheme', {
                      detail: {
                        ...colorTheme,
                      },
                    }),
                  );
                }, 3100);

                console.log('✅ Color themes loaded:', data);
              })
              .catch((err) => {
                console.error('❌ Error loading pattern sets:', err, requestId);
              });
          }

          let turnaroundType = 'reg';
          const defaultTASpec = parsedSku.specs.find(
            (spec: any) => spec.code === TURNAROUND_SPEC_CODE,
          );

          if (defaultTASpec) {
            const productTASpec = product.specs?.find(
              (spec: any) => spec.code === TURNAROUND_SPEC_CODE,
            );
            if (
              productTASpec &&
              productTASpec.specsValue.find((value: any) => value.code === defaultTASpec.value)
            ) {
              turnaroundType = defaultTASpec.value.toLowerCase();
            }
          }

          console.log('🕒 [SINGLE] Turnaround type:', turnaroundType);

          const calcProduct = prepareCalcProd(product, {
            qty: 1,
            selected: true,
            ta: turnaroundType,
            parsedSku,
          });

          console.log('💰 [SINGLE] Calc product prepared:', calcProduct);

          const basePrice = Object.keys(calcProduct.specs)
            .filter((key) => key !== TURNAROUND_SPEC_CODE)
            .reduce((acc, code) => {
              const spec = calcProduct.specs[code];
              return (
                acc +
                this.getSpecAdditionalPrice(spec.adderType, spec.adderValue, calcProduct.basePrice)
              );
            }, +calcProduct.basePrice);

          console.log('💵 [SINGLE] Base price calculated:', basePrice);

          const turnarounds: any = {
            reg: { period: product.regularTurnaround, cost: 0 },
          };

          const turnaroundSpec = product.specs?.find(
            (spec: any) => spec.code === TURNAROUND_SPEC_CODE,
          );
          if (turnaroundSpec) {
            const [regularCost, rushCost] = turnaroundSpec.specsValue.reduce(
              (acc: [number, number], value: any) => {
                const cost = (+basePrice / 100) * value.adderValue;
                if (value.code === 'REG') acc[0] += cost;
                if (value.code === 'RUSH') acc[1] += cost;
                return acc;
              },
              [0, 0],
            );
            turnarounds.reg.cost = regularCost;
            if (
              product.rushTurnaround &&
              turnaroundSpec.specsValue.find((value: any) => value.code === 'RUSH')
            ) {
              turnarounds.rush = { period: product.rushTurnaround, cost: rushCost };
            }
            turnarounds.productSpecs = {
              [product.code]: turnaroundSpec.specsValue.reduce((acc: any, value: any) => {
                acc[value.code] = {
                  adderType: value.adderType,
                  adderValue: value.adderValue,
                };
                return acc;
              }, {}),
            };
          }

          this.stateMng.set('turnarounds', turnarounds);
          this.stateMng.set('calcItems', {
            turnaround: turnaroundType,
            products: {
              [product.code]: calcProduct,
            },
          });
          this.stateMng.set('items', [
            {
              id: product.code,
              quantity: 1,
              selected: true,
              optional: false,
              productGroupId: product.productGroupId,
              product: { ...product, code: product.code.toLowerCase() },
              parsedSku: SKU.parse(this.stateMng.get('SKUs')[product.code]),
            },
          ]);

          this.stateMng.set('rawData', product);
          console.log('✅ [SINGLE] State successfully updated.', requestId);
        })
        .catch((err) => {
          console.error('❌ [SINGLE] Failed to load product:', err, requestId);
          throw err;
        });
    }

    // ===============================
    // 🧺 BUNDLE MODE
    // ===============================
    if (this.mode === Mode.BUNDLE) {
      console.log('🧺 [BUNDLE] Fetching bundle...', requestId);
      const param = new URLSearchParams(window.location.search);
      const isParamSKUExists = param.has('sku');
      // Helper: fetch with retry (maxAttempts = 3 by default, 1000ms delay)
      const fetchWithRetry = (
        url: string,
        options: RequestInit = {},
        maxAttempts = 3,
        retryDelay = 1000,
      ): Promise<any> =>
        new Promise((resolve, reject) => {
          let attempts = 0;
          const tryFetch = () => {
            attempts++;
            console.log(
              `🌐 [BUNDLE] Fetch attempt ${attempts}/${maxAttempts} -> ${url}`,
              requestId,
            );
            const addedParam = attempts > 1 ? `?retry=${attempts}` : '';
            fetch(url + addedParam)
              .then((res) => {
                console.log('🌐 [BUNDLE] Response status:', res.status, requestId);
                if (res.status === 200) {
                  return res.json().then(resolve);
                }
                // parse error body if possible, then throw
                return res
                  .json()
                  .then((errBody) => {
                    const err: any = new Error('Non-200 response');
                    err.status = res.status;
                    err.body = errBody;
                    throw err;
                  })
                  .catch((parseErr) => {
                    // if body couldn't be parsed, still create an error with status
                    const err: any = new Error('Non-200 response (and body parse failed)');
                    err.status = res.status;
                    err.parseErr = parseErr;
                    throw err;
                  });
              })
              .catch((err) => {
                if (attempts < maxAttempts) {
                  console.warn(
                    `⚠️ [BUNDLE] Attempt ${attempts} failed. Retrying in ${retryDelay}ms...`,
                    err,
                    requestId,
                  );
                  setTimeout(tryFetch, retryDelay);
                } else {
                  console.error('❌ [BUNDLE] All attempts failed.', requestId);
                  reject(err);
                }
              });
          };
          tryFetch();
        });

      return fetchWithRetry(
        `${this.options.calculatorAPIUrl}${API_ENDPOINTS.BUNDLE.replace(':0', this.code)}`,
        {
          method: 'GET',
          headers: {
            'X-Request-ID': requestId,
          },
        },
        3,
        1000,
      )
        .then((bundle: any) => {
          console.log('✅ [BUNDLE] Bundle loaded:', bundle.code, requestId);

          this.stateMng.set('title', `Customize Your ${bundle.shortTitle}`);
          this.stateMng.set('bundleCode', bundle.code);
          this.stateMng.set('enableQuantitySwitcher', bundle.enableQuantitySwitcher);
          this.stateMng.set('isApparel', bundle.isApparel);

          const comparePricePercentage = bundle?.bundleStores?.find(
            (s: any) => s.shopCode == this.stateMng.get('shopCode'),
          )?.comparePricePercentage;
          this.stateMng.set('comparePricePercentage', comparePricePercentage ?? 0);

          this.stateMng.set('bundleShortTitle', bundle.shortTitle);
          console.log('🏷️ [BUNDLE] comparePricePercentage:', comparePricePercentage);

          const items = bundle?.['bundleProducts']?.map((item: any, i: number) => {
            const prodCode = item.product.code;
            productSKUCounters[prodCode] =
              productSKUCounters[prodCode] === 0 ? productSKUCounters[prodCode] + 1 : 0;

            const defaultSpecsFromParams = getProductDefaultSpecValue(
              prodCode,
              productSKUCounters[prodCode],
            );
            const defaultSizeSpecValues = this.stateMng.get('defaultPrdSizeSpec');
            const parsedSku = defaultSpecsFromParams || SKU.parse(item.sku);
            if (defaultSizeSpecValues?.[item.product.code]) {
              parsedSku.specs = parsedSku.specs.map((spec) => {
                if (spec.code == 'SZ') {
                  return {
                    code: 'SZ',
                    value: defaultSizeSpecValues?.[item.product.code] || spec.value,
                  };
                }
                return spec;
              });
            }
            return {
              parsedSku,
              id: item['id'],
              quantity: item['qty'],
              optional: item['isOptional'],
              productGroupId: item['productGroupId'],
              title: item['title'],
              product: { ...item.product, code: item.product.code.toLowerCase() },
              selected: isParamSKUExists
                ? defaultSpecsFromParams || !item['isOptional']
                  ? true
                  : false
                : item.selected,
              isHide: item['isHide'],
              specsHideInCalc: item['specsHideInCalc'],
            };
          });

          console.log('🧩 [BUNDLE] Bundle items parsed:', items);
          this.stateMng.set('items', items);

          if (bundle?.isApparel) {
            if (bundle?.patternSets?.length > 1) {
              const defaultPatternSetCode = this.stateMng.get('defaultPatternSet');
              const patternSet =
                defaultPatternSetCode &&
                bundle?.patternSets.find((ps: any) => ps.code === defaultPatternSetCode)
                  ? bundle?.patternSets.find((ps: any) => ps.code === defaultPatternSetCode)
                  : bundle?.patternSets[0];
              this.stateMng.set('selectedPatternSet', patternSet);
              setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent('selectedPatternSet', {
                    detail: {
                      ...patternSet,
                    },
                  }),
                );
              }, 3000);
              setTimeout(() => {
                this.stateMng.set('patternSets', bundle?.patternSets);
              }, 100);

              const defaultColorTheme = this.stateMng.get('defaultColorTheme');
              const colorTheme = defaultColorTheme
                ? patternSet?.colorThemes.find((ct: any) => ct.code === defaultColorTheme)
                : patternSet?.colorThemes?.[0];
              this.stateMng.set('selectedColorTheme', colorTheme || null);

              setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent('selectedColorTheme', {
                    detail: {
                      ...colorTheme,
                    },
                  }),
                );
              }, 3100);
              setTimeout(() => {
                this.stateMng.set('colorThemes', patternSet?.colorThemes);
              }, 100);
            } else {
              // Collect all pattern types from bundle products
              const facePatternTypes = bundle?.bundleProducts
                ?.flatMap(
                  (bp: any) =>
                    bp.product.modelSection.surfaces?.map(
                      (surface: any) => surface.facePatternTypeCode,
                    ) || [],
                )
                .filter(Boolean); // remove undefined/null

              fetch(`${this.options.calculatorAPIUrl}${API_ENDPOINTS.PATTERN_SETS}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ patternFaceTypes: facePatternTypes }),
              })
                .then(async (res) => {
                  return res.json();
                })
                .then((data) => {
                  const defaultPatternSetCode = this.stateMng.get('defaultPatternSet');

                  const patternSet = defaultPatternSetCode
                    ? data.find((ps: any) => ps.code === defaultPatternSetCode)
                    : bundle?.patternSets[0];
                  this.stateMng.set('selectedPatternSet', patternSet);
                  setTimeout(() => {
                    window.dispatchEvent(
                      new CustomEvent('selectedPatternSet', {
                        detail: {
                          ...patternSet,
                        },
                      }),
                    );
                  }, 3000);
                  this.stateMng.set('patternSets', data);
                  console.log('✅ Pattern sets loaded:', data);
                  const defaultColorTheme = this.stateMng.get('defaultColorTheme');
                  const colorTheme = defaultColorTheme
                    ? patternSet?.colorThemes.find((ct: any) => ct.code === defaultColorTheme)
                    : patternSet?.colorThemes?.[0];
                  this.stateMng.set('selectedColorTheme', colorTheme || null);
                  this.stateMng.set('colorThemes', patternSet.colorThemes);
                  setTimeout(() => {
                    window.dispatchEvent(
                      new CustomEvent('selectedColorTheme', {
                        detail: {
                          ...colorTheme,
                        },
                      }),
                    );
                  }, 3100);
                })
                .catch((err) => {
                  console.error('❌ Error loading pattern sets:', err, requestId);
                });
            }
          }

          const hiddenProducts = items?.filter((product: any) => product?.isHide);
          this.stateMng.set('visibleHiddenProducts', {
            hidden: hiddenProducts,
            visible: [],
            state: 'collapse',
          });
          console.log('🙈 [BUNDLE] Hidden products:', hiddenProducts);

          productSKUCounters = {};

          // Extract turnarounds costs
          const [regularCost, rushCost] = bundle?.['bundleProducts']?.reduce(
            (acc: [number, number], item: any) => {
              if (!item.selected) return acc;
              const turnaroundSpec = item.product.specs?.find(
                (spec: any) => spec.code === TURNAROUND_SPEC_CODE,
              );
              if (!turnaroundSpec) return acc;

              turnaroundSpec.specsValue.forEach((value: any) => {
                const cost = item.qty * ((+item.product.basePrice / 100) * value.adderValue);
                if (value.code === 'REG') acc[0] += cost;
                if (value.code === 'RUSH') acc[1] += cost;
              });
              return acc;
            },
            [0, 0],
          ) || [0, 0];

          const turnarounds: State['turnarounds'] = {
            reg: { period: bundle?.regularTurnaround, cost: regularCost },
            productSpecs: bundle?.['bundleProducts']?.reduce((acc: any, item: any) => {
              const turnaroundSpec = item.product.specs?.find(
                (spec: any) => spec.code === TURNAROUND_SPEC_CODE,
              );
              if (!turnaroundSpec) return acc;
              acc[item.id] = turnaroundSpec.specsValue.reduce((acc: any, value: any) => {
                acc[value.code] = {
                  adderType: value.adderType,
                  adderValue: value.adderValue,
                };
                return acc;
              }, {});
              return acc;
            }, {}),
          };

          if (bundle?.rushTurnaround) {
            turnarounds.rush = { period: bundle.rushTurnaround, cost: rushCost };
          }

          console.log('🔁 [BUNDLE] Turnarounds calculated:', turnarounds);

          this.stateMng.set('turnarounds', turnarounds);
          const isRegTA =
            paramSKUs.length > 0
              ? paramSKUs.some((sku) => {
                  const TA = sku.specs.find((sp) => sp.code === TURNAROUND_SPEC_CODE);
                  return TA?.value == 'REG';
                })
              : true; // when we read the spec from api not from route params is by default regular
          this.stateMng.set('calcItems', {
            turnaround: isRegTA ? 'reg' : 'rush',
            products: bundle?.['bundleProducts']?.reduce((acc: any, item: any) => {
              const prodCode = item.product.code;
              productSKUCounters[prodCode] =
                productSKUCounters[prodCode] === 0 ? productSKUCounters[prodCode] + 1 : 0;

              const defaultSpecsFromParams = getProductDefaultSpecValue(
                prodCode,
                productSKUCounters[prodCode],
              );

              const itemSpecs = item.product.specs?.map((sp: any) => {
                const overrideSpec = item['specsHideInCalc']?.find(
                  (oSpec: any) => oSpec.specCode == sp.code,
                );
                if (!overrideSpec) return sp;
                const specsValues = sp.specsValue?.map((sv: any) => {
                  const overrideInCalcSpecValue = overrideSpec.specsValues?.find(
                    (oSV: any) => sv.code == oSV.specValueCode,
                  )?.hideInCalc;
                  return {
                    ...sv,
                    hideInCalc: overrideInCalcSpecValue ?? sv.hideInCalc,
                  };
                });
                return {
                  ...sp,
                  hideInCalc: overrideSpec ? overrideSpec.hideInCalc : sp.hideInCalc,
                  specsValues,
                };
              });
              item.product.specs = itemSpecs;

              const defaultSizeSpecValues = this.stateMng.get('defaultPrdSizeSpec');
              const parsedSku = defaultSpecsFromParams || SKU.parse(item.sku);
              if (defaultSizeSpecValues?.[item.product.code]) {
                parsedSku.specs = parsedSku.specs.map((spec) => {
                  if (spec.code == 'SZ') {
                    return {
                      code: 'SZ',
                      value: defaultSizeSpecValues?.[item.product.code],
                    };
                  }
                  return spec;
                });
              }

              acc[item.id] = prepareCalcProd(item.product, {
                qty: item.qty,
                selected: isParamSKUExists
                  ? defaultSpecsFromParams || !item.isOptional
                    ? true
                    : false
                  : item.selected,
                ta: isRegTA ? 'reg' : 'rush',
                parsedSku: parsedSku,
                groupId: item['productGroupId'],
              });
              return acc;
            }, {}),
          });

          this.stateMng.set(
            'productGroups',
            bundle.productGroups?.map((group: any) => {
              return {
                id: group.id,
                title: group.title,
                items: items?.filter((item: any) => item.productGroupId === group.id),
              };
            }),
          );
          this.stateMng.set('rawData', bundle);
          console.log('✅ [BUNDLE] Raw bundle data saved.', requestId);
        })
        .catch((err) => {
          console.error('❌ [BUNDLE] Failed to load bundle:', err, requestId);
          throw err;
        });
    }

    // ===============================
    // 🧬 PARENT PRODUCT MODE
    // ===============================
    if (this.mode === Mode.PARENT_PRODUCT) {
      console.log('🧬 [PARENT_PRODUCT] Fetching parent product...');
      return fetch(
        `${this.options.calculatorAPIUrl}${API_ENDPOINTS.PARENT_PRODUCT.replace(':0', this.code)}`,
      )
        .then((res) => {
          console.log('🌐 [PARENT_PRODUCT] Response status:', res.status);
          return res.status === 200 ? res.json() : Promise.reject(res.json());
        })
        .then((parentProduct: any) => {
          console.log('✅ [PARENT_PRODUCT] Parent product loaded:', parentProduct.code);
          const param = new URLSearchParams(window.location.search);
          const isParamSKUExists = param.has('sku');
          this.stateMng.set('title', `Customize Your Product`);
          const comparePricePercentage = parentProduct?.productStores?.find(
            (s: any) => s.shopCode == this.stateMng.get('shopCode'),
          )?.comparePricePercentage;
          this.stateMng.set('comparePricePercentage', comparePricePercentage ?? 0);
          console.log('🏷️ [PARENT_PRODUCT] comparePricePercentage:', comparePricePercentage);
          productSKUCounters[parentProduct.code] =
            productSKUCounters[parentProduct.code] === 0
              ? productSKUCounters[parentProduct.code] + 1
              : 0;

          const defaultSpecsFromParams = getProductDefaultSpecValue(
            parentProduct.code,
            productSKUCounters[parentProduct.code],
          );

          const parsedSku = defaultSpecsFromParams || SKU.parse(parentProduct.sku);
          console.log('🔢 [PARENT_PRODUCT] Parsed SKU:', parsedSku);

          const items = [
            {
              parsedSku,
              id: parentProduct?.id || `_ID_${parentProduct?.code}`,
              quantity: 1,
              qty: 1,
              optional: false,
              isSubItem: false,
              sku: parentProduct?.sku,
              productGroupId: parentProduct.productGroupId,
              title: parentProduct.shortTitle,
              product: { ...parentProduct, code: parentProduct.code.toLowerCase() },
              selected: true,
            },
          ];

          if (parentProduct?.children?.length) {
            const childProducts = parentProduct?.['children']?.map((child: any) => {
              const prodCode = child.product.code;

              productSKUCounters[prodCode] =
                productSKUCounters[prodCode] === 0 ? productSKUCounters[prodCode] + 1 : 0;

              const defaultSpecsFromParams = getProductDefaultSpecValue(
                prodCode,
                productSKUCounters[prodCode],
              );

              const parsedSku = defaultSpecsFromParams || SKU.parse(child.sku);
              return {
                parsedSku,
                id: child['id'],
                quantity: 1,
                qty: 1,
                optional: true,
                sku: child.sku,
                productGroupId: child.product['productGroupId'],
                title: child.product['shortTitle'],
                product: { ...child.product, code: child.product.code.toLowerCase() },
                selected: false,
                modelId: child?.modelId,
                model: child?.model,
                isSubItem: true,
                modelSectionId: child?.modelSectionId,
                modelSection: child?.modelSection,
                sizeSpecSections: child?.sizeSpecSections,
                specsHideInCalc: child?.specsHideInCalc,
              };
            });
            items.push(...childProducts);
          }

          this.stateMng.set('items', items);
          console.log('✅ [PARENT_PRODUCT] Items saved to state:', items);

          productSKUCounters = {};
          // Extract turnarounds costs
          const [regularCost, rushCost] = items?.reduce(
            (acc: [number, number], item: any) => {
              if (!item.selected) return acc;
              const turnaroundSpec = item.product.specs?.find(
                (spec: any) => spec.code === TURNAROUND_SPEC_CODE,
              );
              if (!turnaroundSpec) return acc;
              turnaroundSpec.specsValue.forEach((value: any) => {
                const cost = item.qty * ((+item.product.basePrice / 100) * value.adderValue);
                if (value.code === 'REG') acc[0] += cost;
                if (value.code === 'RUSH') acc[1] += cost;
              });
              return acc;
            },
            [0, 0],
          ) || [0, 0];

          const turnarounds: State['turnarounds'] = {
            reg: { period: parentProduct?.regularTurnaround, cost: regularCost },
            productSpecs: parentProduct?.['children']?.reduce((acc: any, item: any) => {
              const turnaroundSpec = item.product.specs?.find(
                (spec: any) => spec.code === TURNAROUND_SPEC_CODE,
              );
              if (!turnaroundSpec) return acc;
              acc[item.id] = turnaroundSpec.specsValue.reduce((acc: any, value: any) => {
                acc[value.code] = {
                  adderType: value.adderType,
                  adderValue: value.adderValue,
                };
                return acc;
              }, {});
              return acc;
            }, {}),
          };

          if (parentProduct?.rushTurnaround) {
            turnarounds.rush = { period: parentProduct.rushTurnaround, cost: rushCost };
          }

          this.stateMng.set('turnarounds', turnarounds);
          const isRegTA =
            paramSKUs.length > 0
              ? paramSKUs.some((sku) => {
                  const TA = sku.specs.find((sp) => sp.code === TURNAROUND_SPEC_CODE);
                  return TA?.value == 'REG';
                })
              : true; // when we read the spec from api not from route params is by default regular
          this.stateMng.set('calcItems', {
            turnaround: isRegTA ? 'reg' : 'rush',
            products: items.reduce((acc: any, item: any) => {
              const prodCode = item.product.code;
              productSKUCounters[prodCode] =
                productSKUCounters[prodCode] === 0 ? productSKUCounters[prodCode] + 1 : 0;

              const defaultSpecsFromParams = getProductDefaultSpecValue(
                prodCode,
                productSKUCounters[prodCode],
              );
              const itemSpecs = item.product.specs?.map((sp: any) => {
                const overrideSpec = item['specsHideInCalc']?.find(
                  (oSpec: any) => oSpec.specCode == sp.code,
                );

                if (!overrideSpec) return sp;
                const specsValues = sp.specsValue?.map((sv: any) => {
                  const overrideInCalcSpecValue = overrideSpec.specsValues?.find(
                    (oSV: any) => sv.code == oSV.specValueCode,
                  )?.hideInCalc;
                  return {
                    ...sv,
                    hideInCalc: overrideInCalcSpecValue ?? sv.hideInCalc,
                  };
                });
                return {
                  ...sp,
                  hideInCalc: overrideSpec ? overrideSpec.hideInCalc : sp.hideInCalc,
                  specsValues,
                };
              });

              item.product.specs = itemSpecs;

              const parsedSku = defaultSpecsFromParams || SKU.parse(item.sku);
              acc[item.id] = prepareCalcProd(item.product, {
                qty: item.qty,
                selected: isParamSKUExists
                  ? defaultSpecsFromParams
                    ? true
                    : false
                  : item.selected,
                ta: isRegTA ? 'reg' : 'rush',
                parsedSku: parsedSku,
                groupId: item['productGroupId'],
              });
              return acc;
            }, {}),
          });

          this.stateMng.set('rawData', parentProduct);
          console.log('✅ [PARENT_PRODUCT] Raw parent data saved.');
        })
        .catch((err) => {
          console.error('❌ [PARENT_PRODUCT] Failed to load:', err);
          throw err;
        });
    }

    console.log('🟡 [load] No mode matched, resolving empty.');
    return Promise.resolve();
  }

  private fetchAndSetPhoneNumber = () => {
    const shopCode = this.stateMng.get('shopCode');
    const url = `${this.options.apiUrl}/stores/${shopCode}`;

    fetch(url)
      .then((res) => {
        if (!res.ok) console.log(`Failed to fetch store info: ${res.status}`);

        return res.json();
      })
      .then((data) => {
        const phoneNumber = data?.phone;

        if (phoneNumber) this.stateMng.set('phoneNumber', phoneNumber);
        else console.warn('Phone number not found in response:', data);
      })
      .catch((err) => {
        console.error('Error fetching store phone number:', err);
      });
  };

  private handleCalcItemChange(calcItems: CalcItems) {
    // update the SKUs
    const skus = Object.keys(calcItems.products || [])
      .filter((key) => calcItems.products[key].selected)
      .reduce((acc: State['SKUs'], key) => {
        acc[key] = SKU.create(calcItems.products[key].code, calcItems.products[key].specs);
        return acc;
      }, {});
    this.stateMng.set('SKUs', skus);

    // update turnarounds
    const turnarounds = this.stateMng.get('turnarounds');

    const [regularTaCost, rushTaCost] = Object.keys(calcItems.products).reduce(
      (acc, prodKey) => {
        const product = calcItems.products[prodKey];
        if (product.selected && product.specs && product.specs?.[TURNAROUND_SPEC_CODE]) {
          const basePrice = this.getProductPrice(product).total;
          if (turnarounds.productSpecs[prodKey]?.['RUSH']) {
            const productTotalPrice = product.qty * basePrice;
            acc[1] +=
              (productTotalPrice / 100) * turnarounds.productSpecs[prodKey]['RUSH'].adderValue;
          }
          if (turnarounds.productSpecs[prodKey]?.['REG']) {
            acc[0] += (basePrice / 100) * turnarounds.productSpecs[prodKey]['REG'].adderValue;
          }
        }
        return acc;
      },
      [0, 0],
    );
    turnarounds.reg.cost = regularTaCost;
    if (turnarounds.rush) {
      turnarounds.rush.cost = rushTaCost;
    }
    this.stateMng.set('turnarounds', turnarounds);

    // update the total
    const turnaroundTotal = turnarounds[calcItems.turnaround]?.cost || 0;

    const total = Object.values(calcItems.products)
      .filter((prod) => prod.selected)
      .reduce((acc, prod) => {
        const productTotalPrice = prod.qty * this.getProductPrice(prod).total;
        return acc + productTotalPrice;
      }, turnaroundTotal);

    const oldTotal = this.stateMng.get('total');

    if (oldTotal !== total) {
      if (oldTotal > total) {
        this.stateMng.set('priceStatus', 'DECREASE');
      } else {
        this.stateMng.set('priceStatus', 'INCREASE');
      }
      const comparePricePercentage = this.stateMng.get('comparePricePercentage');
      const comparePrice =
        comparePricePercentage === 0 ? 0 : total / (1 - comparePricePercentage / 100);
      this.stateMng.set('comparePrice', comparePrice);
      this.stateMng.set('total', total);
    }
  }

  protected handleTurnaroundChange(e: Event) {
    const el = e.target as HTMLInputElement;
    const calcItems = this.stateMng.get('calcItems');
    const prodTASpecs = this.stateMng.get('turnarounds').productSpecs;
    Object.keys(prodTASpecs).forEach((prodKey) => {
      if (el.value === 'rush') {
        const rushSpec = prodTASpecs[prodKey]['RUSH'];
        if (rushSpec) {
          calcItems.products[prodKey].specs[TURNAROUND_SPEC_CODE] = {
            valueCode: 'RUSH',
            adderType: rushSpec.adderType,
            adderValue: rushSpec.adderValue,
          };
        }
      } else {
        const regularSpec = prodTASpecs[prodKey]['REG'];
        if (regularSpec) {
          calcItems.products[prodKey].specs[TURNAROUND_SPEC_CODE] = {
            valueCode: 'REG',
            adderType: regularSpec.adderType,
            adderValue: regularSpec.adderValue,
          };
        }
      }
    });

    calcItems.turnaround = el.value as 'reg' | 'rush';
    this.stateMng.set('calcItems', calcItems);
  }

  private populateInitialUIValues() {
    const calcItems = this.stateMng.get('calcItems');
    const isQtySwitcherEnabled = this.stateMng.get('enableQuantitySwitcher');
    Object.keys(calcItems.products).forEach((prodKey) => {
      // Populate the product price
      const productPrice = this.getProductPrice(calcItems.products[prodKey]).total;
      const priceEl = document.getElementById('price_prd_' + prodKey);
      if (priceEl) {
        const price = this.showPrice(
          calcItems.products[prodKey].qty,
          productPrice,
          calcItems.products[prodKey].selected,
        );
        priceEl.textContent = isQtySwitcherEnabled ? price + ' Each' : price;
      }
    });

    this.__renderActions(this.stateMng.get('actions'));
    this.__renderTurnaround(this.stateMng.get('turnarounds'));
    this.__renderTotal(this.stateMng.get('total'));
    this.__renderComparePrice(this.stateMng.get('comparePrice'));
    if (this.modAdmin) {
      this.__renderAdminBundleUrl(this.stateMng.get('bundleCode'));
      this.__renderAdminSingleUrl(this.stateMng.get('rawData'));
    }
  }

  private __renderTitle(title: State['title']) {
    const titleEl = document.getElementById('calc-header-title');
    const titleCopyEl = document.getElementById('calc-header-title-copy');
    if (titleEl) titleEl.innerHTML = title;
    if (titleCopyEl) titleCopyEl.innerHTML = COPY_CALC_ICON;
  }

  protected getProductPrice(product: CalcItemProduct) {
    const onTotalAdders: CalcItemSpec[] = [];
    const specTotal = Object.keys(product.specs)
      .filter((key) => key !== TURNAROUND_SPEC_CODE)
      .reduce((acc, key) => {
        const spec = product.specs[key];
        if (spec.adderType === 'on_total') {
          onTotalAdders.push(spec);
          return acc;
        }
        return (
          acc + this.getSpecAdditionalPrice(spec.adderType, spec.adderValue, product.basePrice)
        );
      }, 0);
    const onTotalAddersPrice = onTotalAdders.reduce(
      (acc, spec) => acc + ((product.basePrice + specTotal) * spec.adderValue) / 100,
      0,
    );
    const prodTotal = product.basePrice + specTotal + onTotalAddersPrice;
    const multiplierPercentage = product.multiplierPercentage || 0;
    // find the multiplier percentage base on the total price of the product
    let price = 0;
    if (multiplierPercentage !== 0) {
      price =
        multiplierPercentage > 0
          ? prodTotal + (prodTotal * multiplierPercentage) / 100
          : prodTotal - Math.abs((prodTotal * multiplierPercentage) / 100);
    } else price = prodTotal;

    return {
      basePrice: product.basePrice,
      specsTotal: specTotal,
      onTotalAdders: onTotalAddersPrice,
      total: price,
    };
  }

  protected __renderCalcItemSelections(calcItems: State['calcItems']): void {
    Object.keys(calcItems.products).forEach((key) => {
      const product = calcItems.products[key];

      // update the checkbox
      const checkbox = document.querySelector<HTMLInputElement>('#chk_prd_' + key);
      if (checkbox) checkbox.checked = product.selected;
      // update the product header
      const productEl = document.getElementById('prd_' + key);
      if (productEl) productEl.classList.toggle('active', product.selected);

      // update price
      const priceEl = document.getElementById('price_prd_' + key);
      if (priceEl && product.specs) {
        const price = this.getProductPrice(product).total;
        priceEl.textContent = this.showPrice(product.qty, price, product.selected);
      }
    });
  }

  protected __renderGroups(): void {
    // this is used only for compact
  }

  protected __renderPriceTableDOMTree(): void {
    // this is used only for compact
  }

  protected __renderPatternSets(): void {
    // this is used only for compact
  }

  protected __renderColorThemes(): void {
    // this is used only for compact
  }

  protected __renderTurnaround(turnarounds: State['turnarounds']): void {
    if (turnarounds.rush) {
      const rushLabel = document.querySelector<HTMLSpanElement>('#lbl_ta_rush');
      if (rushLabel)
        rushLabel.textContent = `Guaranteed Rush: ${turnarounds['rush']['period']} Business Days`;
    }
  }

  private __renderTotal(total: number): void {
    const priceStatus = this.stateMng.get('priceStatus'); // expected 'INCREASE' or other
    const comparePrice = this.stateMng.get('comparePrice');
    const totalEl = document.getElementById('calc-total');

    if (!totalEl) return;

    const currency = this.stateMng.get('currency');
    const totalWithCurrency = total * currency.rate;

    // class names used for animation
    const OUT_UP = 'slide-out-up';
    const IN_DOWN = 'slide-in-down';
    const OUT_DOWN = 'slide-out-down';
    const IN_UP = 'slide-in-up';

    const isIncrease = priceStatus === 'INCREASE';

    const outClass = isIncrease ? OUT_UP : OUT_DOWN;
    const inClass = isIncrease ? IN_DOWN : IN_UP;

    // remove any of our animation classes (clean slate)
    totalEl.classList.remove(OUT_UP, IN_DOWN, OUT_DOWN, IN_UP);

    // Force reflow so adding the class always restarts the animation.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    void (totalEl as HTMLElement).offsetWidth;

    // Start "out" animation (old text moves away)
    totalEl.classList.add(outClass);

    // Attach one-time listener to run when out animation finishes
    totalEl.addEventListener(
      'animationend',
      () => {
        // 1) Update text (prepare the new displayed value)
        // Detect if there is any fractional cents (safely)
        const roundedToCents = Math.round(totalWithCurrency * 100) / 100;
        const hasDecimal = Math.round((roundedToCents - Math.floor(roundedToCents)) * 100) !== 0;

        totalEl.textContent = `${currency.symbol}${roundedToCents.toLocaleString('en-US', {
          minimumFractionDigits: hasDecimal ? 2 : 0,
          maximumFractionDigits: 2,
        })}`;

        // 2) Color for comparePrice (you had "red" when comparePrice !== 0)
        if (comparePrice !== 0) {
          (totalEl as HTMLElement).style.color = 'red';
        } else {
          (totalEl as HTMLElement).style.color = '';
        }

        // 3) Swap classes: remove out class, force reflow, add in class to animate the new value in
        totalEl.classList.remove(outClass);

        // Force reflow again to restart the next animation reliably
        void (totalEl as HTMLElement).offsetWidth;

        totalEl.classList.add(inClass);

        // Optional: If you want to clean up the inClass after it finishes (so element doesn't carry animation classes)
        totalEl.addEventListener(
          'animationend',
          () => {
            totalEl.classList.remove(inClass);
          },
          { once: true },
        );
      },
      { once: true },
    );
  }

  private __renderComparePrice(comparePrice: number): void {
    const priceStatus = this.stateMng.get('priceStatus');
    const comparePriceEl = document.getElementById('calc-total-compare-at');
    if (!comparePriceEl) return;

    const currency = this.stateMng.get('currency');
    const comparePriceWithCurrency = comparePrice * currency.rate;

    // keep your original behavior: round up to the next 10 when showing compare price
    const newText =
      comparePrice === 0
        ? ''
        : `${currency.symbol}` +
          (Math.ceil(comparePriceWithCurrency / 10) * 10).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });

    // animation class names (same set as __renderTotal)
    // class names used for animation
    const OUT_UP = 'slide-out-up';
    const IN_DOWN = 'slide-in-down';
    const OUT_DOWN = 'slide-out-down';
    const IN_UP = 'slide-in-up';

    const isIncrease = priceStatus === 'INCREASE';

    const outClass = isIncrease ? OUT_UP : OUT_DOWN;
    const inClass = isIncrease ? IN_DOWN : IN_UP;

    // clean any previous animation classes
    comparePriceEl.classList.remove(OUT_UP, IN_DOWN, OUT_DOWN, IN_UP);

    // force reflow so animation restarts predictably
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    void (comparePriceEl as HTMLElement).offsetWidth;

    // start "out" animation (old value moves away)
    comparePriceEl.classList.add(outClass);

    comparePriceEl.addEventListener(
      'animationend',
      () => {
        // update text after out animation completes
        comparePriceEl.textContent = newText;

        // if newText is empty, we are hiding the compare price — cleanup and stop
        if (newText === '') {
          comparePriceEl.classList.remove(outClass);
          return;
        }

        // otherwise animate the new value in
        comparePriceEl.classList.remove(outClass);

        // force reflow before starting "in" animation
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        void (comparePriceEl as HTMLElement).offsetWidth;

        comparePriceEl.classList.add(inClass);

        // cleanup in-class after it finishes
        comparePriceEl.addEventListener(
          'animationend',
          () => {
            comparePriceEl.classList.remove(inClass);
          },
          { once: true },
        );
      },
      { once: true },
    );
  }

  private __renderActions(actions: State['actions']) {
    if (actions) {
      const actionsEl = document.getElementById('calc-footer-actions');
      if (actionsEl) {
        actionsEl.innerHTML = '';
        actionsEl.append(...actions);
      }
    }
  }

  protected __renderAdminBundleUrl(bundleCode: State['bundleCode']) {
    const bundleUrlEl = document.getElementById('admin_bundle_url_bundle_pdp');
    if (bundleUrlEl) {
      bundleUrlEl.replaceChildren(
        createElement({
          tag: 'a',
          attributes: {
            href: `${this.options.adminUrl}/bundles/${bundleCode}`,
            target: '_blank',
            class: 'calc-header__title__templateUrl__link',
          },

          children: ['Bundle'],
        }),
      );
    }
  }

  protected __renderAdminSingleUrl(product: State['rawData']) {
    const productUrlEl = document.getElementById('admin_product_url_single_pdp');
    if (productUrlEl) {
      productUrlEl.replaceChildren(
        createElement({
          tag: 'a',
          attributes: {
            href: `${this.options.adminUrl}/products/${product?.code}`,
            target: '_blank',
            class: 'calc-header__title__templateUrl__link',
          },

          children: ['Product'],
        }),
      );
    }
  }

  protected getSpecAdditionalPrice(
    adderType: string,
    adderValue: number,
    prodBase: number,
  ): number {
    if (adderValue === 0) return 0;
    if (adderType === 'on_price') return adderValue;
    if (adderType === 'on_base') return (adderValue * prodBase) / 100;
    return 0;
  }

  private toggleBundleProduct(e: Event) {
    const el = e.target as HTMLElement;
    if ('target' in el.dataset) {
      const targetKey = el.dataset['target']?.substring(4);
      if (targetKey) {
        const items = this.stateMng.get('calcItems');
        items.products[targetKey].selected = !items.products[targetKey].selected;
        this.stateMng.set('calcItems', items);
      }
    }
  }

  protected handleSpecChange(isInGroup = false, e: Event) {
    const el = e.target as HTMLSelectElement;
    const productKey = el.closest('.calc-spec')?.getAttribute('id')?.substring(4);
    const specCode = el.dataset['code'];

    if (!productKey || !specCode) {
      console.warn('Product or spec key not found');
      return;
    }
    const calcItems = this.stateMng.get('calcItems');
    const value = JSON.parse(el.value);

    if (specCode === QUANTITY_SPEC_CODE) {
      calcItems.products[productKey].qty = +value.code;
    }

    calcItems.products[productKey].specs[specCode] = {
      valueCode: value.code,
      adderType: value.adderType,
      adderValue: value.adderValue,
    };

    this.stateMng.set('calcItems', calcItems);
  }

  protected showPrice(quantity: number, price: number, selected: boolean) {
    const currency = this.stateMng.get('currency');
    if (currency?.rate) {
      price = price * currency.rate;
    }
    if (!selected) return `From ${currency?.symbol}` + (price * quantity).toFixed(2);
    const fixedPrice = price.toFixed(2);
    return quantity === 1
      ? `${currency?.symbol}` + fixedPrice
      : quantity + ' x ' + `${currency?.symbol}` + fixedPrice;
  }

  protected getTurnaroundDOMTree() {
    const turnarounds = this.stateMng.get('turnarounds');
    const turnaround = this.stateMng.get('calcItems').turnaround;
    const turnaroundItems: ElementInfo[] = [
      {
        tag: 'label',
        children: [
          {
            tag: 'input',
            attributes: {
              type: 'radio',
              value: 'reg',
              name: `npt_turnaround`,
              checked: turnaround === 'reg' ? 'true' : undefined,
            },
            events: { change: this.handleTurnaroundChange.bind(this) },
          },
          {
            tag: 'span',
            children: [`Regular: ${turnarounds['reg']['period']} Business Days`],
          },
        ],
      },
    ];

    if (turnarounds?.rush) {
      turnaroundItems.push({
        tag: 'label',
        children: [
          {
            tag: 'input',
            attributes: {
              type: 'radio',
              value: 'rush',
              name: `npt_turnaround`,
              checked: turnaround === 'rush' ? 'true' : undefined,
            },
            events: { change: this.handleTurnaroundChange.bind(this) },
          },
          {
            tag: 'span',
            attributes: { id: 'lbl_ta_rush' },
            children: [`Guaranteed Rush: ${turnarounds['rush']['period']} Business Days `],
          },
        ],
      });
    }

    return turnaroundItems;
  }

  private getSpecsDOMTree(specs: ProductSpecs[], defaultValues: ParsedSku['specs']): ElementInfo[] {
    return specs?.map((spec) => {
      const specValue = defaultValues.find((value) => value.code === spec.code);
      const uid = Math.floor(Math.random() * 9 ** 5);

      return {
        tag: 'div',
        attributes: {
          class: 'calc-spec__content__item',
          style: spec.hideInCalc ? 'display: none;' : undefined,
        },
        children: [
          {
            tag: 'label',
            attributes: {
              for: `slct_spec_${spec.key}_${uid}`,
            },
            children: [spec.title],
          },
          {
            tag: 'select',
            attributes: {
              id: `slct_spec_${spec.key}_${uid}`,
              'data-code': spec.code,
            },
            events: { change: this.handleSpecChange.bind(this, false) },
            children: spec.specsValue?.map((value) => {
              if (value.hideInCalc) return '';
              return {
                tag: 'option',
                attributes: {
                  value: JSON.stringify(value),
                  selected: value.code === specValue?.value ? '' : undefined,
                  'data-code': value.code.toLowerCase(),
                },
                children: [value.title],
              };
            }),
          },
        ],
      };
    });
  }

  protected getSingleDOMTree(item: CalculatorItem): ElementInfo[] {
    const defaultSpecParams = this.getParamSKUs();
    const skuFromParam = defaultSpecParams.find(
      (sku) => sku.code.toLowerCase() === item.product.code.toLowerCase(),
    );

    // set proper template url id
    const tmplUrl = document.getElementById('tmpl_url_single_pdp');
    if (tmplUrl) tmplUrl.id = `tmpl_url_${item.id}`;

    return [
      {
        tag: 'div',
        attributes: {
          class: `calc-spec`,
          id: `prd_${item.id}`,
        },
        children: this.getSpecsDOMTree(
          item.product.specs?.filter((spec) => spec.code !== TURNAROUND_SPEC_CODE),
          skuFromParam?.specs || item.parsedSku.specs,
        ),
      },
    ];
  }

  protected getBundleDOMTree(items: CalculatorItem[]): ElementInfo[] {
    const paramSKUs = this.getParamSKUs();
    const productSKUCounters: Record<string, number> = {};

    const getProductDefaultSpecValue = (productCode: string, counter: number): ParsedSku => {
      const sku = paramSKUs.filter((sku) => sku.code.toLowerCase() === productCode.toLowerCase());
      return sku?.[counter];
    };

    return items?.map((item) => {
      productSKUCounters[item.product.code] =
        productSKUCounters[item.product.code] === 0 ? productSKUCounters[item.product.code] + 1 : 0;

      const defaultSpecs =
        getProductDefaultSpecValue(item.product.code, productSKUCounters[item.product.code])
          ?.specs || item.parsedSku.specs;

      return {
        tag: 'div',
        attributes: {
          class:
            `calc-spec ${item.selected ? 'active' : ''} ${item.product.specs?.filter((spec) => spec.code !== TURNAROUND_SPEC_CODE).length ? '' : 'empty-specs'}`.trim(),
          id: `prd_${item.id}`,
        },
        children: [
          {
            tag: 'div',
            attributes: {
              class: `calc-spec__header ${!item.optional ? 'disabled' : ''}`.trim(),
              'data-target': `prd_${item.id}`,
            },
            events: { click: item.optional ? this.toggleBundleProduct.bind(this) : undefined },
            children: [
              {
                tag: 'div',
                attributes: { class: 'calc-spec__header__title' },
                children: [
                  {
                    tag: 'label',
                    attributes: { class: 'calc-spec__header__switch' },
                    children: [
                      {
                        tag: 'input',
                        attributes: {
                          type: 'checkbox',
                          id: `chk_prd_${item.id}`,
                          checked: item.selected,
                        },
                      },
                      { tag: 'span', attributes: { class: 'calc-spec__header__switch__slider' } },
                    ],
                  },
                  {
                    tag: 'p',
                    attributes: { class: 'calc-spec__header__title__text' },
                    children: [item.product.shortTitle],
                  },
                  {
                    tag: 'span',
                    attributes: {
                      class: 'calc-spec__header__title__templateUrl',
                      id: `tmpl_url_${item.id}`,
                    },
                    children: [],
                  },
                ],
              },
              {
                tag: 'p',
                attributes: {
                  class: 'calc-spec__header__pricing',
                  id: `price_prd_${item.id}`,
                },
                children: [''],
              },
            ],
          },
          {
            tag: 'div',
            attributes: {
              class: 'calc-spec__content',
              'data-code': item.product.code,
            },
            children: this.getSpecsDOMTree(
              item.product.specs?.filter((spec) => spec.code !== TURNAROUND_SPEC_CODE) || [],
              defaultSpecs,
            ),
          },
        ],
      };
    });
  }

  protected getErrorDOMTree() {
    return {
      tag: 'div',
      attributes: { class: 'calc-error', style: 'text-align:center; padding:20px;' },
      children: [
        {
          tag: 'button',
          attributes: {
            class: 'retry-button',
            style:
              'background-color:#FFC83A; color:#000000; border:none; padding:10px 20px; border-radius:6px; cursor:pointer; font-size:14px;',
            onclick: 'window.location.reload()',
          },
          children: ['Load Price Calculator'],
        },
        {
          tag: 'div',
          attributes: {
            style: 'margin-top:10px; color:#333; font-size:14px;',
          },
          children: [
            'Need help? Call ',
            {
              tag: 'a',
              attributes: {
                href: `tel:${formatPhoneNumber(this.stateMng.get('phoneNumber'), { separator: '', showPlusOne: true })}`,
                style: 'color:#007bff; text-decoration:none;',
              },
              children: [
                formatPhoneNumber(this.stateMng.get('phoneNumber'), {
                  separator: '-',
                  showPlusOne: false,
                }),
              ],
            },
          ],
        },
      ],
    };
  }
}

if (typeof window !== 'undefined') {
  window['Calculator'] = Calculator;
}
